import { useState } from "react";

import { BasicModal, Button } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { useCountryUpdate } from "app/pages/activation/hooks/home/useCountryUpdate";
import useSaveSettingsMutation from "app/pages/activation/queries/settings/useSaveSettingsMutation";
import type { ActivationObjectSchema } from "app/pages/activation/types/api";
import type { ICountryObject } from "app/services/CountryService";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { generateAutomationName } from "../../../helpers/generateAutomationName";
import { CountryDropdown } from "../../Shared/CountryDropdown/CountryDropdown";
import { CountryDisplay } from "../../Shared/Visual/CountryDisplay/CountryDisplay";

import { MODAL_BUTTON_WIDTH, MODAL_POPUP_HEIGHT, modalStylesConfig } from "./config";
import * as SC from "./styles";
import { getMessage, SAVE, TITLE } from "./text";

export const CountryUpdateModal = ({ activationObject }: { activationObject: ActivationObjectSchema }) => {
    const { setIsOpen, isOpen, availableCountries } = useCountryUpdate(!!activationObject?.sharedBy);
    const { mutate: saveSettings } = useSaveSettingsMutation(activationObject?.id);

    const hasMultipleCountries = availableCountries.length > 1;

    const defaultCountry = availableCountries[0];
    const [country, setCountry] = useState(defaultCountry);

    const onClick = (countryObject: ICountryObject) => setCountry(countryObject);

    const onSave = async () => {
        saveSettings({
            country: country?.id,
            id: activationObject?.id,
            domain: activationObject?.primaryDomain,
            competitorsDomains: activationObject?.competitorsDomains,
            name: activationObject?.name,
            trafficSource: activationObject?.trafficSource,
        });

        setIsOpen(false);

        await proNavigator.updateParams({ country: country.id });
    };

    return (
        <BasicModal
            isOpen={isOpen}
            customStyles={modalStylesConfig}
            dataAutomation={generateAutomationName("alerts", ["home", "modal", "invalid", "country"])}
            closeIconConfig={{ show: false }}
        >
            <SC.Title>{translate(TITLE)}</SC.Title>
            <SC.Body>
                <SC.Message>{translate(getMessage(hasMultipleCountries), { name: activationObject?.name })}</SC.Message>

                {hasMultipleCountries ? (
                    <SC.CountryWrapper className="country-wrapper">
                        <CountryDropdown
                            buttonWidth={MODAL_BUTTON_WIDTH}
                            onClick={onClick}
                            selectedCountryId={country?.id}
                            appendTo=".country-wrapper"
                            hasSearch
                            dropdownPopupHeight={MODAL_POPUP_HEIGHT}
                            countries={availableCountries}
                        />
                    </SC.CountryWrapper>
                ) : (
                    <CountryDisplay
                        id={defaultCountry?.id}
                        name={defaultCountry?.text}
                    />
                )}

                <SC.Footer>
                    <Button onClick={onSave}>{translate(SAVE)}</Button>
                </SC.Footer>
            </SC.Body>
        </BasicModal>
    );
};
